// src/components/ChatPanel.jsx
import { useState, useEffect, useRef } from "react";
import { PHASES, ROLES } from "../utils/constants.js";

// Per-phase channel config, gated by the viewer's role.
// Players who lack the role for a private channel get an observer view:
// same channel node is read (so we can render a placeholder), but the
// messages themselves are hidden so roles can't be deduced from chat.
function getChannelView(phase, me) {
  const isMafia  = me.role === ROLES.MAFIA;
  const isDoctor = me.role === ROLES.DOCTOR;
  const alive    = !!me.alive;

  if (phase === PHASES.NIGHT) {
    if (isMafia) {
      return {
        channel: "night",
        label: "🔪 Mafia Channel",
        canChat: alive,
        canView: true,
        dotColor: "#e05555",
        msgColor: "#e05555",
        placeholder: alive ? "Coordinate with your fellow Mafia..." : "Observing (deceased)...",
      };
    }
    return {
      channel: "night",
      label: "🌙 Night Falls",
      canChat: false,
      canView: false,
      dotColor: "rgba(100,100,100,0.5)",
      msgColor: "rgba(150,150,150,0.5)",
      placeholder: "The Mafia is whispering in the shadows...",
      lockedReason: "Only the Mafia can hear this channel.",
    };
  }

  if (phase === PHASES.SAVE) {
    if (isDoctor) {
      return {
        channel: "save",
        label: "💊 Doctor Channel",
        canChat: alive,
        canView: true,
        dotColor: "#4db8b8",
        msgColor: "#4db8b8",
        placeholder: alive ? "Who will you save tonight?" : "Observing (deceased)...",
      };
    }
    return {
      channel: "save",
      label: "🤫 A Hushed Prayer",
      canChat: false,
      canView: false,
      dotColor: "rgba(100,100,100,0.5)",
      msgColor: "rgba(150,150,150,0.5)",
      placeholder: "The Doctor is deliberating...",
      lockedReason: "Only the Doctor can hear this channel.",
    };
  }

  if (phase === PHASES.DAY) {
    return {
      channel: "day",
      label: "☀️ Town Square",
      canChat: alive,
      canView: true,
      dotColor: "var(--gold)",
      msgColor: "var(--gold)",
      placeholder: alive ? "Share your suspicions..." : "You are dead — observing only.",
    };
  }

  // LOBBY / REVEAL / RESULT / ENDED: passive feed
  return {
    channel: "all",
    label: "📋 Game Feed",
    canChat: false,
    canView: true,
    dotColor: "rgba(201,168,76,0.4)",
    msgColor: "var(--gold-dim)",
    placeholder: "Awaiting next phase...",
  };
}

export default function ChatPanel({ state, me, localChat, onSendChat, style }) {
  const [input, setInput] = useState("");
  const chatRef           = useRef(null);
  const phase             = state.phase;

  const view     = getChannelView(phase, me);
  const { channel, label, canChat, canView, dotColor, msgColor, placeholder, lockedReason } = view;

  const messages = canView ? (localChat[channel] || []) : [];

  // Auto-scroll on new messages
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages.length, channel]);

  function handleSend() {
    const text = input.trim();
    if (!text || !canChat) return;
    setInput("");
    onSendChat(channel, text);
  }

  return (
    <div style={{
      border: "1px solid rgba(201,168,76,0.14)",
      borderRadius: 3,
      display: "flex",
      flexDirection: "column",
      background: "rgba(255,255,255,0.01)",
      overflow: "hidden",
      ...style,
    }}>
      {/* Header */}
      <div style={{ padding: "0.55rem 0.9rem", background: "rgba(201,168,76,0.05)", borderBottom: "1px solid rgba(201,168,76,0.11)", fontSize: "0.62rem", letterSpacing: "0.2em", color: "var(--gold-dim)", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
        {label}
      </div>

      {/* Messages */}
      <div ref={chatRef} style={{ flex: 1, overflowY: "auto", padding: "0.65rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.35rem", minHeight: 0 }}>
        {!canView && (
          <div style={{ color: "rgba(242,234,216,0.28)", fontSize: "0.75rem", fontStyle: "italic", textAlign: "center", marginTop: "1rem", lineHeight: 1.7, padding: "0 0.5rem" }}>
            {placeholder}
            {lockedReason && (
              <div style={{ marginTop: "0.5rem", fontSize: "0.65rem", color: "rgba(242,234,216,0.2)", letterSpacing: "0.1em" }}>
                {lockedReason}
              </div>
            )}
          </div>
        )}
        {canView && messages.length === 0 && (
          <div style={{ color: "rgba(242,234,216,0.2)", fontSize: "0.72rem", fontStyle: "italic", textAlign: "center", marginTop: "1rem" }}>
            No messages yet...
          </div>
        )}
        {canView && messages.map((m, i) => {
          const isSystem = m.type === "system";
          return (
            <div key={i} style={{ fontSize: "0.76rem", lineHeight: 1.5, color: isSystem ? "rgba(201,168,76,0.55)" : "var(--paper)", fontStyle: isSystem ? "italic" : "normal", wordBreak: "break-word" }}>
              {!isSystem && (
                <span style={{ fontWeight: 700, marginRight: "0.3rem", color: msgColor }}>
                  {m.author}:
                </span>
              )}
              {m.text}
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div style={{ display: "flex", borderTop: "1px solid rgba(201,168,76,0.11)", padding: "0.45rem", gap: "0.4rem", flexShrink: 0 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          disabled={!canChat}
          placeholder={canChat ? placeholder : (canView ? "Observing..." : "You cannot speak here.")}
          maxLength={200}
          style={{
            flex: 1,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(201,168,76,0.18)",
            borderRadius: 2,
            padding: "0.4rem 0.6rem",
            color: "var(--paper)",
            fontFamily: "'Courier Prime', monospace",
            fontSize: "0.78rem",
            outline: "none",
            opacity: canChat ? 1 : 0.45,
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canChat}
          style={{
            background: "rgba(201,168,76,0.14)",
            border: "1px solid rgba(201,168,76,0.28)",
            color: "var(--gold)",
            borderRadius: 2,
            padding: "0.4rem 0.65rem",
            cursor: canChat ? "pointer" : "not-allowed",
            fontSize: "0.8rem",
            opacity: canChat ? 1 : 0.3,
            flexShrink: 0,
            transition: "background 0.15s",
          }}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
